/**
 * Every customer-facing string the Work surfaces add in YAG-372, in one file.
 *
 * Same reason as `workers/copy.ts`: the copy rules are test-pinned, and a test
 * can only pin strings it can enumerate. `workCopy.test.ts` walks everything
 * exported here; `renderedCopy.test.tsx` sweeps what actually reaches the
 * screen, because a banned word slips into a JSX literal just as easily.
 *
 * The rules (scoping addendum section 1, binding):
 *   - the noun for the people who act on gates is **editor**; the noun for the
 *     named crafts is **worker**. Never persona, agent, employee, hire,
 *     container, Mission, Commitment, or tier.
 *   - workers are they/them.
 *   - no em-dashes. The separator is a middot.
 *   - autonomy words are lowercase: training / supervised / autonomous.
 *   - no fabricated statistics. If a number is not counted somewhere, it is not
 *     printed anywhere.
 */
import {
  acceptProposalEffect,
  type AcceptedHoldKind,
  type AgentReviewState,
  type ItemVerb,
  type JobAuthority,
  PLAN_AUTONOMOUS_FOLLOWS,
  type PlanSelfReviewCheckKey,
  type PlanSelfReviewDelta,
  type PlanSelfReviewOutcome,
  type RunPurpose,
  settleSummarySentence,
  STEERING_NOTE_HANDED_LINE,
  type WorkItemGitHubState,
  type WorkItemPrConflict,
  type WorkItemPrMainMerged,
} from "@yagni/shared";
import { PLAN_REVIEW_SLUG } from "../workers/crafts.js";

/* ── the list ── */

/* ── the three views ──
   One row grammar read three ways (simplification, 2026-09-24). The stage words
   are `workStage.PIPELINE_STAGE_LABEL`; what follows is everything the header,
   the views and their groups say around them. */

/* The search, last in the filter row. It finds any item the reader may see,
   archived ones too, by title words, our id or tracker key, or PR number. */
export const SEARCH_LABEL = "Search Work";

/* ── the three empty states ── */

/* ── Add tickets: the picker over the connected trackers ── */

/* ── the pane ── */

/* ── the live run (YAG-373) ── */

export const RUN_LABEL = "The run";

/** Under the run header, while it is going. */
export const RUN_LIVE_NOTE = "running the approved plan · corrections here apply immediately";

/** Under the run header once the attempt has stopped. */
export const RUN_SETTLED_NOTE = "this run has stopped · the log below is what it did";

/**
 * The steps came from the plan a person approved, and nothing recorded which of
 * them ran. Said plainly rather than drawn as ticks nobody wrote down.
 */
export const RUN_STEPS_UNTRACKED_NOTE =
  "the plan an editor approved · this run does not report progress step by step";

/** No steps could be read at all. The log below is still the truth. */
export const RUN_NO_STEPS_NOTE = "running the approved plan";

/**
 * What the run is FOR, in the head beside the worker.
 *
 * Three runs can stand behind one item and they are not the same act: one works
 * the plan out, one carries it out, one reads the repository before either is
 * possible. Printing the word means the head cannot be misread as "the build is
 * happening" when what is happening is a read.
 */
export const RUN_PURPOSE_LABEL: Record<RunPurpose, string> = {
  planning: "working the plan out",
  execution: "building the approved plan",
  scoping: "reading the code first",
};

/**
 * The no-steps line for a run that was never going to have plan steps.
 *
 * `RUN_NO_STEPS_NOTE` covers the execution case, where a plan exists and could
 * not be read. A planning or scoping run has no steps because there is no plan
 * yet, and saying so is not the same admission.
 */
export const RUN_NO_STEPS_BY_PURPOSE: Record<RunPurpose, string> = {
  planning: "no steps to draw yet · this run is working the plan out",
  execution: RUN_NO_STEPS_NOTE,
  scoping: "no steps to draw · this run reads the code before there is a plan",
};

/**
 * The outputs the run recorded. There is no empty-state string on purpose: a
 * run that recorded nothing renders no block at all, the same rule the commit
 * trail follows.
 */
export const RUN_OUTPUTS_LABEL = "what it produced";

export const RUN_LOG_LABEL = "the log";
export const RUN_LOG_EMPTY = "nothing has been logged for this run yet";

export const RUN_COMMITS_LABEL = "commits";
/**
 * Why the commits are a list of their own instead of a note under each step:
 * the run records a commit for the attempt, not for the step, so pinning one to
 * a step would be an attribution nobody made.
 */
export const RUN_COMMITS_NOTE =
  "recorded for the run, not per step, so they are listed rather than pinned to one";

/** "started 08:20". Empty when the run has no start time to print. */
export function runStartedLine(startedAt: string | null): string {
  if (!startedAt) return "";
  const when = new Date(startedAt);
  if (Number.isNaN(when.getTime())) return "";
  return `started ${when.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })}`;
}

/* ── the executing pane ──
   While the work is running the page answers one question, and it is not "how
   does the pipeline work". It is: what did I approve, and is it still going.
   Everything that answers a different question moved behind a disclosure or
   onto the run surface, which is where the machine facts belong. */

/* ── the attempt: one run, folded to one line ── */
export const ATTEMPT_LABEL = "This run";
export const ATTEMPT_STEPS_LABEL = "Steps";
export const ATTEMPT_PRODUCED_LABEL = "Produced";
export const ATTEMPT_LOG_LABEL = "Log · this run only";
export const ATTEMPT_LOG_EMPTY = "This run recorded no events.";
export const ATTEMPT_LOG_UNREADABLE =
  "The log could not be read. Open the full run record to see it.";
export const ATTEMPT_OUTPUTS_UNREADABLE = "What it kept could not be read";
export const ATTEMPT_RECORD_LINK = "Full run record";
export const ATTEMPT_CANDIDATE_KEPT = "candidate kept";
export const ATTEMPT_NO_PR = "no PR";
export const ATTEMPT_NOTHING_PRODUCED = "nothing kept";
/**
 * The one-attempt lane's setup ask on an attempt no session ever ran: what
 * the run line says instead of a failed build that started and ended at once.
 */
export function attemptBeforeWorkLine(attemptNo: number | null | undefined): string {
  const which = typeof attemptNo === "number" ? `Attempt ${attemptNo}` : "The attempt";
  return `${which} stopped before any work started: the repository has no prepared environment.`;
}
/** "4 of 4 steps built", or "4 steps" when the source does not track progress. */
export function attemptStepsFact(done: number, total: number, tracked: boolean): string {
  if (total === 0) return "";
  if (!tracked) return `${total} ${total === 1 ? "step" : "steps"}`;
  return `${done} of ${total} ${total === 1 ? "step" : "steps"} built`;
}

/* ── the plan reader's shaped modes ── */
/** The heading over the plan's own sections, once the decision sits above them. */
export const PLAN_BODY_LABEL = "The plan";
/** Grounding, How this plan was made and Critic findings, as one fold. */
export const PLAN_HOW_MADE_LABEL = "How it was made";

/** The record-mode questions fold, once every question has an answer on record. */
export const PLAN_ANSWERS_LABEL = "Your answers";
/** The same fold when Reeve answered any of them on an autonomous approval. */
export const PLAN_ANSWERS_WITH_REEVE_LABEL = "Answers";

/* ── the clamp: a long block's first lines, then "more" ── */
export const CLAMP_MORE = "more";
export const CLAMP_LESS = "less";

/* ── the stage-driven body ──
   One stage, one thing on screen. The heading names what a reader is looking
   at, so it stops reading "The ask" over a document nobody is being asked
   about. */

/* ── shaping a proposal before a plan is written ──
   The proposed state has almost nothing to do and it is the last point the work
   can be shaped cheaply: once a plan exists, changing it costs a whole planning
   pass. The rail is already the place that can turn a conversation into a
   change, so this only focuses it. */

/* ── the split the build ran (implement fan-out) ── */

export const FANOUT_LABEL = "the split";

/**
 * Why the section says this out loud: a builder that never reported is simply
 * absent here. Nothing on this surface is filled in on a child's behalf.
 */
export const FANOUT_NOTE =
  "each builder as the run recorded it · a builder that reported nothing is left out rather than guessed at";

/** The bounded repair loop's ceiling. Printed, so it is never a mystery. */
export const FANOUT_FIX_CAP = 3;

/**
 * The decision line: how the build was split, and the reason the run gave for
 * splitting it that way. A single-writer run is a decision too, so it reads as
 * one rather than as an empty fan.
 */
export function fanoutDecisionLine(decision: {
  mode: "fan" | "single";
  width?: number;
  reason: string;
}): string {
  const head =
    decision.mode === "fan"
      ? decision.width
        ? `fanned ${decision.width} ways`
        : "fanned across builders"
      : "ran as one builder";
  const reason = decision.reason.trim();
  return reason ? `${head} · ${reason}` : head;
}

/**
 * "6 paths · done". The count is printed only when one was recorded, and it is
 * the number of PATHS the workstream claimed (a claim is often a directory
 * covering many files), so the copy says "paths" rather than miscounting files.
 */
export function fanoutChildNote(child: {
  state: "running" | "done" | "failed";
  files?: number;
}): string {
  const claimed =
    typeof child.files === "number" && child.files > 0
      ? `${child.files} path${child.files === 1 ? "" : "s"}`
      : null;
  return claimed ? `${claimed} · ${child.state}` : child.state;
}

/** "fix pass 2 of 3 · 3 findings · re-engaged backend-routes". */
export function fanoutFixTurnLine(turn: {
  turn: number;
  findings: number;
  reengaged: string[];
}): string {
  const findings = `${turn.findings} finding${turn.findings === 1 ? "" : "s"}`;
  const named = turn.reengaged.filter((name) => name.trim().length > 0);
  const who = named.length > 0 ? ` · re-engaged ${named.join(", ")}` : "";
  return `fix pass ${turn.turn} of ${FANOUT_FIX_CAP} · ${findings}${who}`;
}

/* ── the needs-input decision (YAG-373) ── */

/** A plain question's footer: the answer, and the way to say it is not needed. */
export const PLAIN_ASK_SEND = "Send response";
export const PLAIN_ASK_EFFECT =
  "Your answer goes on the item's record, and the item moves on from the question.";
export const PLAIN_ASK_DISMISS = "Not needed · close the item";
export const PLAIN_ASK_DISMISS_EFFECT = "Closes the item. Nothing more runs on it.";
/** The closed-pull-request stop: the plan runs again, or the ticket closes. */
export const CLOSED_PR_RUN_AGAIN_EFFECT =
  "Running again builds a new pull request from the approved plan.";
export const CLOSED_PR_CLOSE_VERB = "Close this ticket";
export const CLOSED_PR_CLOSE_EFFECT = "Closes the ticket. Nothing more runs on it.";
/** The receipts, read off what the server says it did with the answer. */
export const PLAIN_ASK_BACK_IN_PLAN =
  "Answer saved. The item is back in Plan. Start the plan when you're ready.";
export const PLAIN_ASK_QUEUED = "Answer sent, the work is queued for its next pass.";
export const PLAIN_ASK_SAVED = "Answer saved.";

/** The receipt for a plain question's answer: only what the server reported. */
export function plainAskReceipt(response: { queued?: boolean; phase?: string } | null): string {
  if (response?.queued === true) return PLAIN_ASK_QUEUED;
  if (response?.phase === "planning") return PLAIN_ASK_BACK_IN_PLAN;
  return PLAIN_ASK_SAVED;
}

/* ── the ask a gate carries (`payload.ask`) ──
   Written by the side that opened the gate, from what it recorded, so the dock
   and the stop card say the same true thing. */

/** The stop card's label: whose run stopped, when the case file knows. */
export function askStoppedLabel(name: string | null | undefined): string {
  const who = name?.trim();
  return who ? `Why ${who} stopped` : "Why the run stopped";
}
/** A paused-run gate written before stops carried an ask. */
export const ASK_UNRECORDED_LINE = "The run did not record why it stopped. What it did is below.";
export const ASK_JUMP = "Jump to the decision ↓";
export const ASK_OUTPUT_SHOW = "output";
/** The label on every link to an existing pull request: the ask, the sheet footers and menus. */
export function viewPrLabel(prNumber: number | null | undefined): string {
  return prNumber != null ? `View PR #${prNumber}` : "View pull request";
}
export const ASK_OUTPUT_HIDE = "hide output";

/** The need line's lead: whose ask this is, when the case file knows. */
export function askNeedLead(name: string | null | undefined): string {
  const who = name?.trim();
  return who ? `${who} needs` : "This needs";
}
/** The dock shows the first facts; the rest are in the card above. */
export function askMoreFacts(n: number, name?: string | null): string {
  return `${n} more in ${askStoppedLabel(name)} above`;
}

/* ── the gate footer ── */

/**
 * The footer's label: the ask, named. Label, one consequence sentence, the
 * secondary verb, then the primary rightmost (simplification, 2026-09-24).
 */
export const GATE_LABEL = {
  proposal: "Accept proposal",
  plan: "Approve plan",
  stopped: "Stopped to ask",
  merge: "Merge",
  review: "Post review",
  delivery: "Delivery",
  fix: "Fix round",
  other: "Decide",
} as const;

/** The plan's consequence, and its questions counted on the end of it. */
export const PLAN_APPROVE_CONSEQUENCE =
  "Starts a build on a branch. Nothing reaches main until a person merges.";
export function planQuestionsLine(answered: number, asked: number): string {
  return `${Math.min(answered, asked)} of ${asked} ${asked === 1 ? "question" : "questions"} answered.`;
}
/** Why Approve is dark while a question is open. */
export const PLAN_QUESTIONS_OPEN = "Answer the plan's questions in the plan above to approve it.";
/** Why Approve is dark when the plan names nothing to build. */
export const PLAN_NO_FILES =
  "This plan names no file to change, so there is nothing to build yet. Send it back with a note on where the change lives.";
/** A plan whose file list still holds placeholders ("TBD", "<id>"): Approve stays dark. */
export const PLAN_PLACEHOLDER_FILES =
  "This plan's file list still holds placeholders, so it cannot be built as written. Send it back so the files are named.";
/** A step only a person can do, flagged on the approve gate. */
export function personOnlyStepLine(step: number, why: string): string {
  return `Step ${step} needs a person · ${why}`;
}
/**
 * A plan the build would run with no check at all: no runnable command in the
 * plan and no verify command on the repository. It holds a Team's own
 * approval; a person may still approve, so the line says what that means.
 */
export const PLAN_NO_CHECKS =
  "This plan lists no command that proves the change works, and the repository has no verify command. Send it back with one, or approve knowing the build runs no checks.";
export function planNoChecksLine(suggested: readonly string[]): string {
  return suggested.length > 0
    ? `${PLAN_NO_CHECKS} The reading of the repository ran and passed: ${suggested.join(" · ")}.`
    : PLAN_NO_CHECKS;
}
/** The plan's secondary: it runs the next planning pass. */
export const PLAN_SEND_BACK_VERB = "Send back";

/** A ticket in Review with a pull request: merge it, or ask for another round. */
export const MERGE_VERB = "Merge";
export const MERGE_ON_GITHUB = "Merge on GitHub";
export const MERGE_MERGING = "Merging…";
export const MERGE_CONSEQUENCE =
  "Irreversible on main. Merge is always a person's click. Request changes leaves your notes for the next round.";
export const MERGE_ON_GITHUB_CONSEQUENCE =
  "Merge it on GitHub, where the pull request is; it reaches Done when the merge lands. Request changes leaves your notes for the next round.";
export const MERGE_NO_PR = "The pull request is not open yet. It shows here the moment it is.";

/** Why the YAGNI Review App must be installed, wherever its install link is offered. */
export const REVIEW_APP_REASON =
  "Proctor's approval only counts with YAGNI Review installed with Contents: write.";

/**
 * What GitHub says about the pull request, one sentence. The page never infers
 * an approval from draft state: only GitHub's review decision and the people
 * who approved the current head say it is approved.
 */
export function gitHubStateLine(github: WorkItemGitHubState | null | undefined): string {
  if (!github || github.stale) return "View the pull request to see its review state.";
  if (github.reviewDecision === "CHANGES_REQUESTED") return "Changes were requested on GitHub.";
  if (github.approvedBy.length > 0) return `Approved on GitHub by ${github.approvedBy.join(", ")}.`;
  if (github.reviewAppApproved) {
    return github.reviewDecision === "REVIEW_REQUIRED"
      ? `Approved on GitHub by YAGNI Review, but GitHub does not count it yet. ${REVIEW_APP_REASON}`
      : "Approved on GitHub by YAGNI Review.";
  }
  if (github.reviewDecision === "REVIEW_REQUIRED") return "GitHub needs a person's approval.";
  return "GitHub requires no review for this pull request.";
}

/** The pull request's state chip on the sheet, in GitHub's terms. A conflict with the base outranks the review state. */
export function gitHubStateChip(
  github: WorkItemGitHubState | null | undefined,
  conflict?: WorkItemPrConflict | null,
): string {
  if (conflict) {
    const base = conflict.base ?? "base";
    return conflict.fixer === "wright" ? `merging ${base}` : `conflicts with ${base}`;
  }
  if (!github || github.stale) return "open";
  if (github.reviewDecision === "CHANGES_REQUESTED") return "changes requested";
  if (github.approvedBy.length > 0) return "approved on GitHub";
  if (github.reviewDecision === "REVIEW_REQUIRED") return "needs approval";
  if (github.reviewAppApproved) return "approved by YAGNI Review";
  return gitHubAllowsMerge(github) ? "ready to merge" : "open";
}

/** The chip's tone: working while Wright merges, the honesty amber while a conflict waits on a person. */
export function gitHubStateChipTone(
  conflict?: WorkItemPrConflict | null,
): "open" | "working" | "conflict" {
  if (!conflict) return "open";
  return conflict.fixer === "wright" ? "working" : "conflict";
}

/** A time on the sheet: the hour today, the day otherwise ("14:20", "Oct 24"). */
function shortTime(iso: string, now: Date): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  if (at.toDateString() === now.toDateString()) {
    return at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  }
  return at.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** "main", or what to call a base GitHub did not name. */
function baseOf(fact: { base: string | null }): string {
  return fact.base ?? "the base branch";
}

/**
 * The pull request conflicts with its base: one line that says since when and
 * who fixes it. The numbers are Wright's own (`prSignals/mergeWatch.ts`: a
 * week after the hand-off, ten merges).
 */
export function prConflictLine(conflict: WorkItemPrConflict, now: Date = new Date()): string {
  const base = baseOf(conflict);
  const since = conflict.since ? shortTime(conflict.since, now) : "";
  const head = `Conflicts with ${base}${since ? ` since ${since}` : ""}.`;
  switch (conflict.fixer) {
    case "wright":
      return `${head} Wright is merging ${base} in now.`;
    case "window_passed":
      return `${head} Wright merges ${base} on its own for 7 days after it hands a pull request over. That time has passed.`;
    case "merge_net":
      return `${head} Wright already merged ${base} in 10 times on its own for this pull request, so it stops here.`;
    case "ask_open":
      return `Also conflicts with ${base}${since ? ` since ${since}` : ""}. Keep going merges ${base} in, in the same push.`;
    case "person":
      return head;
  }
}

/** Wright no longer merges the base in on its own, so the person may start the round. */
export function canFixConflicts(conflict: WorkItemPrConflict | null | undefined): boolean {
  return conflict?.fixer === "window_passed" || conflict?.fixer === "merge_net";
}

export const FIX_CONFLICTS_VERB = "Fix the conflicts";

/** The footer when the person may start the merge round. */
export function fixConflictsWhy(conflict: WorkItemPrConflict): string {
  const base = baseOf(conflict);
  return `This pull request conflicts with ${base}. Wright can merge ${base} in and resolve it: one fix round${
    conflict.roundCost ? `, usually ${conflict.roundCost}` : ""
  }.`;
}

/** The footer while Wright merges the base in on its own. */
export function mergingBaseWhy(conflict: WorkItemPrConflict): string {
  return `${baseOf(conflict)} moved under this pull request. Wright is merging it in, then CI and Proctor run again.`;
}

/** What Wright's merge push did, as the ask's facts: the merge, and an approval it dismissed. */
export function mainMergedFacts(
  merged: WorkItemPrMainMerged,
  now: Date = new Date(),
): Array<{ tone: "ok" | "info"; text: string }> {
  const time = shortTime(merged.at, now);
  return [
    { tone: "ok", text: `Wright merged ${baseOf(merged)} in${time ? ` at ${time}` : ""}.` },
    ...(merged.approvalDismissed
      ? [
          {
            tone: "info" as const,
            text: "Your approval was dismissed by that push, as this repository asks. Approve again.",
          },
        ]
      : []),
  ];
}

/**
 * What Wright's ask about its pull request adds from the pull request itself:
 * a merge it just pushed, and a conflict with the base and who fixes it.
 */
export function pullRequestAskFacts(
  merge:
    | { conflict?: WorkItemPrConflict | null; mainMerged?: WorkItemPrMainMerged | null }
    | null
    | undefined,
  now: Date = new Date(),
): Array<{ tone: "ok" | "bad" | "info"; text: string }> {
  return [
    ...(merge?.mainMerged ? mainMergedFacts(merge.mainMerged, now) : []),
    ...(merge?.conflict
      ? [{ tone: "bad" as const, text: prConflictLine(merge.conflict, now) }]
      : []),
  ];
}

/** The footer when a merge push dismissed the approval. */
export function approvalDismissedWhy(merged: WorkItemPrMainMerged): string {
  return `Wright merged ${baseOf(merged)} in. GitHub needs your approval again.`;
}

/** GitHub would merge the current head now: no review missing, nothing blocking. Unknown is no. */
export function gitHubAllowsMerge(github: WorkItemGitHubState | null | undefined): boolean {
  if (!github || github.stale) return false;
  if (github.reviewDecision === "REVIEW_REQUIRED" || github.reviewDecision === "CHANGES_REQUESTED")
    return false;
  return (
    github.mergeableState === "clean" ||
    github.mergeableState === "unstable" ||
    github.mergeableState === "has_hooks"
  );
}

/** What Wright did with every review point, in a sentence or two; null before the head settles. The ask and the timeline use the same words. */
export const settleSummaryLine = settleSummarySentence;

export function mergedLine(sha: string | null): string {
  return sha
    ? `Merged as ${sha.slice(0, 7)}. It reaches Done when GitHub confirms the merge.`
    : "Merged. It reaches Done when GitHub confirms the merge.";
}
export const MERGE_STAMP = "MERGED";
export const MERGE_FAILED = "Could not merge. Try again, or merge it on GitHub.";
export const REQUEST_CHANGES_VERB = "Request changes";
export const REQUEST_CHANGES_PLACEHOLDER = "What should the next round change?";
export const REQUEST_CHANGES_STAMP = "CHANGES REQUESTED";
export const REQUEST_CHANGES_SENT =
  "Wright is revising the pull request with your notes. Proctor reviews it again after.";
export const REQUEST_CHANGES_FAILED = "The fix round did not start. Try again.";
/** A Team pull request still a draft (opened before 2026-10-19): it leaves draft by itself once its checks and Proctor's review settle. */
export const DRAFT_WAIT_CONSEQUENCE =
  "Draft until checks pass and Proctor's review settles. It leaves draft by itself then.";
/** A draft published from a build that did not pass everything. */
export const DRAFT_HELD_UNVERIFIED_CONSEQUENCE =
  "This build did not pass everything, so it stays a draft until a fix round passes, or you take it out of draft on GitHub.";
/** The pull request settled past a commit status YAGNI cannot read. */
export const MERGE_UNREADABLE_STATUS_NOTE =
  "One commit status did not pass and YAGNI can't read it · often a Vercel preview, which needs the commit author to have a seat on your Vercel team. GitHub shows it on the pull request.";
/** Proctor approved, and the review line holds that for a person: the build ticket points at it. */
export const HELD_APPROVAL_LABEL = "Confirm review";
export const HELD_APPROVAL_VERB = "Confirm";
export const HELD_APPROVAL_CONFIRMING = "Confirming…";
export const HELD_APPROVAL_CONSEQUENCE =
  "Proctor approved this push. Confirm to post the approval and take the pull request out of draft.";
export const HELD_APPROVAL_READ = "Read the review";
export const HELD_APPROVAL_STAMP = "CONFIRMED";
export const HELD_APPROVAL_CONFIRMED =
  "Proctor's approval is posted and the pull request left draft. Merge it when you are ready.";
/** The approval posted, but its build did not pass everything, so a person takes it out of draft. */
export const HELD_APPROVAL_KEPT_DRAFT =
  "Proctor's approval is posted. The pull request stays a draft because its build did not pass everything; read what failed on it, then take it out of draft on GitHub.";
export const HELD_APPROVAL_FAILED =
  "The confirm did not go through. Try again, or open the review.";
/** Held on a review this reader cannot open: true, and nothing to press here. */
export const HELD_APPROVAL_ELSEWHERE =
  "Proctor approved this push. Someone who can open its review confirms it, and that takes the pull request out of draft.";

/** A delivery with no pull request (a document, a CLI handoff). */
export const DELIVERY_DONE_VERB = "Mark done";
export const DELIVERY_DONE_EFFECT =
  "Marking it done puts this delivery on the record. Request changes sends your notes back for another pass.";
export const DELIVERY_DONE_HANDED_OFF = "Done and on the record.";
export const DELIVERY_DONE_STAMP = "DONE";

/**
 * Who holds an accepted item that is not planning (P0.1), in the words the
 * row prints. The capacity line names credits on purpose: the owner chose the
 * plain fact over the YAG-339 no-meter wording for this one surface. The
 * backend's capacity reason strings keep the ceiling language.
 */
export function acceptedHoldWords(hold: {
  kind: AcceptedHoldKind;
  personName?: string | null;
}): string {
  switch (hold.kind) {
    case "capacity":
      return "Waiting on credits";
    case "person":
      return `With ${hold.personName?.trim() || "a person"}`;
    case "team_paused":
      return "Team paused";
    case "operator_paused":
      return "Planning paused by an operator";
    case "waiting_turn":
      return "Waiting for a free slot · starts on its own";
  }
}

/** The stamp word per resolved gate kind. */
export const GATE_STAMPS = {
  accept_proposal: "ACCEPTED",
  approve_execution_plan: "APPROVED",
  review_result: "POSTED",
  needs_input: "ANSWERED",
  verify_receipt: "VERIFIED",
  commission_project: "COMMISSIONED",
  milestone_decomposition: "PUSHED",
} as const;

/**
 * The standing consequence (management loop): what this signature does to the
 * worker's record on this Team, said at the moment of signing. The trust
 * ledger already counts every resolve; this line just stops that being a
 * secret. Null keeps the card plain when no worker carries the work.
 */
export function gateRecordNote(kind: string, workerName: string | null): string | null {
  if (!workerName) return null;
  if (kind === "approve_execution_plan") {
    return (
      `your sign-off lands on ${workerName}'s record on this Team · ` +
      "a clean record is what you read before you make this autonomous"
    );
  }
  return null;
}

/** "by Ami · Jul 24, 08:35" under a stamp. Neutral when the name is unknown. */
export function stampByLine(name: string | null, when: string | null): string {
  const who = name ? `by ${name}` : "recorded";
  return when ? `${who} · ${when}` : who;
}

/* ── the plan changes waiting on a reviewer (conversational plan gate) ── */

export const AMENDMENTS_LABEL = "Proposed changes";

/**
 * Why the note says "nothing is applied until you accept": the cards sit under
 * a document the reviewer has already been told is read only, so the obvious
 * fear is that something is being changed underneath them. It is not.
 */
export const AMENDMENTS_NOTE =
  "each change is a proposal against the plan below · nothing is applied until you accept";

/** The one committing verb, and what it does. Both page-level, by design. */
export const AMENDMENT_ACCEPT_VERB = "Accept changes";
export const AMENDMENT_ACCEPT_EFFECT =
  "Accepting applies the remaining changes and records a new revision.";

/** The only per-card verb: a quiet exclusion, never a commit. */
export const AMENDMENT_REJECT_VERB = "Reject";

export const AMENDMENT_WHY_LABEL = "why";
/** The two halves of a diff row, in the material language of the surface. */
export const AMENDMENT_CURRENT_LABEL = "now";
export const AMENDMENT_PROPOSED_LABEL = "proposed";
/** The heading a change to the page's opening lines is filed under. */
export const AMENDMENT_OPENING_SECTION = "the opening";
/** Said once, so the collapsed sections do not read as a rendering failure. */
export const AMENDMENT_UNCHANGED_NOTE = "sections this change does not touch are left out";

export const AMENDMENT_REJECTED_NOTE = "rejected by you · left out of the batch";
export const AMENDMENT_APPLIED_STAMP = "APPLIED";
export const AMENDMENT_APPLIED_NOTE = "applied to the plan · recorded as a revision";

export const AMENDMENT_STALE_NOTE = "The plan changed since this was proposed.";
export const AMENDMENT_LAPSED_NOTE =
  "The review ended before this one was decided, so it was never applied.";

export const AMENDMENT_DIFF_LOADING = "Reading the plan page.";
export const AMENDMENT_DIFF_UNAVAILABLE =
  "The proposed wording is no longer stored for this change, so only the reason is shown.";
export const AMENDMENT_PAGE_UNREADABLE =
  "The plan page could not be read here, so this change is shown by its reason only.";
export const AMENDMENT_ACCEPT_FAILED =
  "Those changes did not apply, and the plan is unchanged. Read the reason above and try again.";
export const AMENDMENT_REJECT_FAILED = "That rejection did not land. Try again.";

/**
 * Whose change this is, when the payload says.
 *
 * A change the staff engineer proposed off their own read is not the same
 * object as one the rail typed on a reader's behalf, and the card has been
 * saying "why" over both. `proposedBy` is optional and absent on every
 * rail-proposed entry, so an entry that names nobody keeps the label it has
 * always had rather than inheriting an attribution nobody made.
 */
export const AMENDMENT_REEVE_LABEL = "Reeve's change to the plan";

export function amendmentWhyLabel(proposedBy: string | null | undefined): string {
  return proposedBy === PLAN_REVIEW_SLUG ? AMENDMENT_REEVE_LABEL : AMENDMENT_WHY_LABEL;
}

/* ── the staff engineer's read of the plan ─────────────────────────────────
   A second worker read this plan before it reached a person, and until now
   the only trace of that on the case file was an amendment card with no
   author. The read gets its own pane between the plan and the decision, in
   the reading order a pull request uses: the work, the review of the work,
   then the button. Print material, because the read is a committed record —
   the glass below is where the undecided thing lives. */

/** A watch-set slug as words: `money-paths` reads "money paths". */
export function watchSetWords(slug: string): string {
  return slug.replace(/[-_]+/g, " ").trim();
}

/**
 * "5 concerns weighed · 1 raised · money paths".
 *
 * Both numbers are counted server-side (the engagement's watch set, and what
 * this read actually raised against it), so the line prints what was counted
 * and nothing else. An older gate carries no watch set at all and gets no
 * line; a read that weighed nothing has nothing to report either.
 */
export function reeveWatchSetLine(weighed: number, raised: readonly string[]): string {
  if (!Number.isFinite(weighed) || weighed <= 0) return "";
  const named = raised.map(watchSetWords).filter((word) => word.length > 0);
  const head = `${weighed} concern${weighed === 1 ? "" : "s"} weighed`;
  return named.length > 0
    ? `${head} · ${named.length} raised · ${named.join(", ")}`
    : `${head} · none raised`;
}

/* ── the review the reviewer posted, on the build item's own case file ──
   The build item carries the pull request; the review of that pull request
   lived only on the review case file, one hop away. It reads here as what it
   is: a named worker's review of the work above it. */

/** Proctor's latest judgment of one of its threads, as the panel marks it. */
export const REVIEW_THREAD_JUDGMENT: Readonly<Record<string, string>> = {
  resolved: "Proctor: fixed",
  answered: "Proctor: answered",
  treated_addressed: "Proctor: addressed",
  persists: "Proctor: still open",
  partially_addressed: "Proctor: partly fixed",
};
export const REVIEW_THREAD_WRIGHT_REPLIED = "Wright replied";
/** The two groups of Proctor's threads, as GitHub holds them. */
export function reviewThreadsGroupLabel(resolved: boolean, count: number): string {
  return `${resolved ? "Resolved" : "Open"} on GitHub · ${count}`;
}

/**
 * "3 findings not posted on GitHub". Only when the report recorded the count — an older
 * report that never wrote one prints no line rather than a confident zero.
 */
export function discardedFindingsLine(dropped: number | null | undefined): string {
  if (typeof dropped !== "number" || !Number.isFinite(dropped) || dropped <= 0) return "";
  // Discarded before the review posted: GitHub never saw them.
  return `${dropped} finding${dropped === 1 ? "" : "s"} not posted on GitHub`;
}

/* ── what this item cost, as a sentence ───────────────────────────────────
   The runs roll-up already knows the total and which worker each run belongs
   to. Said as one line it answers the question a buyer actually asks, which
   is not "how many runs" but "what did this cost me, and who spent it". Every
   number is the sell side the roll-up already carries; nothing here is
   derived, estimated, or apportioned. */

export const ITEM_COST_LEAD = "This item";
export const ITEM_COST_RATE_CARD = "on the rate card";

/**
 * "This item · $3.84 on the rate card · Wright $3.12 · Proctor $0.31".
 *
 * Empty when the total is zero: a free item has no cost sentence, and "$0.00"
 * is a claim about pricing rather than a fact about this work. A share whose
 * own total rounds to nothing is left out for the same reason.
 */
export function itemCostLine(
  total: string | null,
  shares: readonly { name: string; amount: string | null }[],
): string {
  if (!total) return "";
  const parts = [`${ITEM_COST_LEAD} · ${total} ${ITEM_COST_RATE_CARD}`];
  for (const share of shares) {
    if (share.amount) parts.push(`${share.name} ${share.amount}`);
  }
  return parts.join(" · ");
}

/* ── the plan's open questions (the question block at the foot of the plan) ──
   The plan already asks these questions in prose; what it never did was let a
   person answer one. The block below the plan body is where an answer is made,
   and the rules the copy has to carry are the same two the surface is built on:
   an answer STAGES (nothing regenerates, nothing runs), and an option the
   planner recommends is marked as recommended rather than dressed up as the
   only real choice. */

/**
 * "open questions · 3" — the mono label at the foot of the plan.
 *
 * The count is the questions on screen, counted, so a reader can tell at a
 * glance how much is being asked of them before they start reading. Zero
 * never reaches here: a plan that asks nothing renders no block at all.
 */
export function planQuestionsLabel(count: number): string {
  return `open questions · ${count}`;
}

/**
 * Why the note says "nothing is regenerated": the questions sit under a
 * document a reader has just been told is read only, so the fear an answer
 * triggers is that the plan is about to rewrite itself under them. It is not.
 *
 * It rides the label as its hover title rather than printing: it explains a
 * fact the surface already shows, and an explanation on the page is one more
 * sentence between the reader and the question.
 */
export const PLAN_QUESTIONS_NOTE =
  "answering one stages your answer · the plan is not regenerated and nothing runs until you decide below";

/** The mark on the option the plan itself would take. A label, never a size. */
export const PLAN_QUESTION_RECOMMENDED_LABEL = "recommended";
/** Every other option the plan weighed. Same card, different label. */
export const PLAN_QUESTION_ALTERNATIVE_LABEL = "alternative";

/**
 * An option carries a reason or it says it has none. A blank line under an
 * answer reads as a rendering failure; this reads as the truth it is.
 */
export const PLAN_QUESTION_NO_REASON = "no reason was recorded for this option";

/**
 * What picking an option changes in the plan, in one line under its reason:
 * "Picking this adds frontend/src/LinePage.tsx to the files · changes step 3". Approval
 * applies it; the reader sees it before choosing. Adds and edits only. Plain
 * text: the card is not markdown.
 */
export function planChangeLine(
  change:
    | {
        addFiles?: readonly string[];
        addSteps?: readonly unknown[];
        editSteps?: ReadonlyArray<{ step: number }>;
        addChecks?: readonly string[];
        addAcceptanceCriteria?: readonly string[];
      }
    | null
    | undefined,
): string | null {
  if (!change) return null;
  const parts: string[] = [];
  const files = change.addFiles ?? [];
  if (files.length) {
    const named = files.slice(0, 2).join(", ");
    const more = files.length > 2 ? ` and ${files.length - 2} more` : "";
    parts.push(`adds ${named}${more} to the files`);
  }
  const steps = change.addSteps?.length ?? 0;
  if (steps) parts.push(steps === 1 ? "adds a step" : `adds ${steps} steps`);
  for (const edit of change.editSteps ?? []) parts.push(`changes step ${edit.step}`);
  const checks = change.addChecks?.length ?? 0;
  if (checks) parts.push(checks === 1 ? "adds a check" : `adds ${checks} checks`);
  const criteria = change.addAcceptanceCriteria?.length ?? 0;
  if (criteria)
    parts.push(
      criteria === 1 ? "adds an acceptance criterion" : `adds ${criteria} acceptance criteria`,
    );
  return parts.length ? `Picking this ${parts.join(" · ")}` : null;
}

/*
 * What was said at a plan gate, and where it stands (spec 2026-11-02, "The
 * plan a person approves is the only contract"). Each answer, note and reply
 * is written into the plan as a new version before Approve is offered.
 */

/** The kind of input a status line is about. */
export type PlanInputNoun = "answer" | "note" | "reply" | "undo";

/** While Reeve writes it into the plan. */
export function planInputApplyingLine(askerName: string, noun: PlanInputNoun): string {
  if (noun === "undo") return `${askerName} is taking your answer out of the plan`;
  if (noun === "reply") return `${askerName} is updating the plan for the reply on the ticket`;
  return `${askerName} is updating the plan for your ${noun}`;
}

/** Once a version holds it: "Plan v4: removed step 4, 6 fewer files". */
export function planInputAppliedLine(version: number | null, summary: string | null): string {
  const said = summary?.trim().replace(/\.$/, "") ?? "";
  if (version == null) return said ? `In the plan: ${said}` : "In the plan";
  return said ? `Plan v${version}: ${said}` : `Plan v${version}`;
}

/** The edit failed or ran out of time, and the server gave no reason. */
export const PLAN_INPUT_FAILED_FALLBACK = "This could not be applied to the plan.";
/** The edit says the input needs a new plan, and the server gave no reason. */
export const PLAN_INPUT_NEEDS_START_OVER_FALLBACK =
  "This needs a new plan, not an edit to this one.";
export const PLAN_INPUT_RETRY_VERB = "Retry";
export const PLAN_INPUT_START_OVER_VERB = "Start over with this";
export const PLAN_INPUT_DISMISS_VERB = "Drop it";
/** Keeping an answer Reeve could not apply as its decision line: no edit, no model call. */
export const PLAN_INPUT_KEEP_AS_DECISION_VERB = "Keep as a decision";
export const PLAN_INPUT_KEEP_AS_DECISION_HINT =
  "The plan keeps your answer as a decision without rewriting its steps. The build and the review still follow it.";
/** Dismissing an Undo that did not apply: the plan keeps the answer it still holds. */
export const PLAN_UNDO_DISMISS_VERB = "Keep my answer";
export const PLAN_ANSWER_CHANGE_VERB = "Change";
export const PLAN_ANSWER_UNDO_VERB = "Undo";
/** Who an answered question's answer is from. */
export const PLAN_ANSWER_BY_PERSON = "Answered";
export function planAnswerByReeve(askerName: string): string {
  return `${askerName}'s choice`;
}
export const PLAN_NOTE_BY_PERSON = "Your note";
export const PLAN_REPLY_ON_TICKET = "Reply on the ticket";
export const PLAN_UNDO_LABEL = "Undo";
/** A typed answer on a stated decision is saved with this, never as it is typed. */
export const PLAN_ANSWER_SAVE_VERB = "Use this answer";

/** The Send back note box at a plan gate: what the note does now. */
export const SEND_BACK_NOTE_PLACEHOLDER = "What should change in the plan?";
export const SEND_BACK_NOTE_EFFECT = "Reeve edits the plan for your note.";

/** The plan's version, and what made it: "Plan v3 · updated for your answer". */
export function planVersionLine(number: number, cause: string): string {
  const why: Record<string, string> = {
    planned: "as Reeve wrote it",
    backfill: "as Reeve wrote it",
    answer: "updated for your answer",
    option_pick: "updated for your answer",
    reeve_answer: "updated for Reeve's answer",
    undo: "updated for your Undo",
    note: "updated for your note",
    tracker_reply: "updated for a reply on the ticket",
    ask_edit: "updated for a change from Ask",
  };
  const said = why[cause];
  return said ? `Plan v${number} · ${said}` : `Plan v${number}`;
}
export const PLAN_VERSION_WHAT_CHANGED = "What changed";
export const PLAN_VERSION_HIDE_CHANGES = "Hide changes";
export const PLAN_VERSION_FIRST = "This is the plan as Reeve wrote it.";
export const PLAN_VERSION_LOADING = "Reading the change.";
export const PLAN_VERSION_UNREADABLE = "The change could not be read. Try again.";
export const PLAN_VERSION_NO_CHANGE = "No section of the plan's page changed in this version.";
/** The diff's side labels: the version before, and this one. */
export function planVersionSides(number: number): { removed: string; added: string } {
  return { removed: `v${number - 1}`, added: `v${number}` };
}

/**
 * A question the planner has no view on. Said out loud rather than left as a
 * gap: an empty space under a question looks like a load that never finished.
 */
export const PLAN_QUESTION_NO_RECOMMENDATION =
  "no recommendation · the plan weighed this one and did not land on an answer";

/** The stepper's two ways through the questions. */
export const PLAN_QUESTION_PREV = "Previous";
export const PLAN_QUESTION_NEXT = "Next question";
/** "2 of 6 answered", on the block's head. */
export function planAnsweredCount(answered: number, count: number): string {
  return `${answered} of ${count} answered`;
}

/**
 * The questions as a RECORD: the fold row's teaser when the plan is not the
 * thing being decided (a paused run, an approved plan, a finished item). It
 * counts what is on the record and claims nothing else: a plan whose answers
 * rode a send-back has no comment per question, so it reads "5 questions"
 * rather than "0 of 5 answered", which would be the surface saying nobody
 * decided when somebody did.
 */
export function planQuestionsTeaser(count: number, answered: number): string {
  const questions = `${count} ${count === 1 ? "question" : "questions"}`;
  return answered > 0 ? `${questions} · ${answered} answered` : questions;
}
export const PLAN_QUESTIONS_RECORD_SHOW = "Show";
export const PLAN_QUESTIONS_RECORD_HIDE = "Hide";

export const PLAN_QUESTION_OTHER = "Something else";
export const PLAN_QUESTION_OTHER_PLACEHOLDER = "Your answer to this question…";
export const PLAN_QUESTION_OTHER_SUBMIT = "Stage this answer";
export const PLAN_QUESTION_OTHER_CANCEL = "Cancel";
export const PLAN_QUESTION_ASK_YAGNI = "Ask @yagni";
/**
 * The reason an answer staged from the rail carries. It is the honest one: the
 * words are @yagni's, and the reader chose to stand behind them.
 */
export const PLAN_ANSWER_FROM_YAGNI = "worked out with @yagni";
/** The rail's verb on a reply, while the conversation is about a plan question. */
export const RAIL_USE_AS_ANSWER = "Use as answer";
/** The same button once the reply is staged: it sent nothing, it staged. */
export const RAIL_USED_AS_ANSWER = "Staged as the answer";

/** The state a question wears once it has been answered but nothing has run. */
export const PLAN_ANSWER_STAGED_LABEL = "answered · staged";
export const PLAN_ANSWER_STAGED_NOTE =
  "staged, not applied · it rides the next pass when you send the plan back below";
export const PLAN_ANSWER_DROP_VERB = "Drop";
/** The hover on the option a reader picked: the same click takes it back. */
export const PLAN_ANSWER_UNDO_HINT = "Chosen · click again to take it back";

/* ── accepting a proposal is starting the plan ──
   The verb used to read "Accept proposal", which named the reviewer's posture
   and not the consequence. `workItems/armOnAccept.ts` sets `due_at = now()` in
   the SAME statement that lands the accepted phase, and both accept paths do
   it, so acceptance IS the start of the planning turn: the evaluator claims the
   item on its next tick and a best-effort wake usually beats the tick. A verb
   that says "accept" over a write that starts work is the same class of small
   lie as a checkmark nobody wrote down. */

export const ACCEPT_PROPOSAL_VERB = "Start plan";

export const ACCEPT_PROPOSAL_EFFECT = acceptProposalEffect(null);

/* ── who approves the plan, by the Plan line's rung ──
   A Supervised Plan line brings the plan back for a person's sign-off; an
   Autonomous one has Reeve approve it and the build starts. Every sentence
   that promises a sign-off reads the rung, and an unknown rung keeps the
   sign-off words, which were the only words before the rung was read. */

/** The bold line under Start plan and Hand to Reeve. */
export function planSignOffBold(planAuthority: JobAuthority | null | undefined): string {
  return planAuthority === "autonomous"
    ? "Reeve approves the plan on its own, and the build follows."
    : "Nothing touches the repository until you approve the plan.";
}

/** The footer's sentence over Start plan (or Retry plan) on a ticket with no plan yet. */
export function startPlanWhy(
  retry: boolean,
  planAuthority: JobAuthority | null | undefined,
): string {
  if (planAuthority === "autonomous") {
    return retry
      ? "The last planning pass did not finish. Retrying drafts a new plan, and Reeve approves it on its own."
      : PLAN_AUTONOMOUS_FOLLOWS;
  }
  return retry
    ? "The last planning pass did not finish. Retrying drafts a new plan for your sign-off."
    : "Starting the plan brings one back here for your sign-off.";
}

/* ── a tracker ticket on its way to Reeve (2026-10-19) ──
   A ticket from Jira or Linear (assigned to yagni, a Team's first tickets,
   Start) waits on nobody: Bailey reads it against the code, posts the spec on
   the ticket, and Reeve plans it. The reading is the check, not a wait (Jack,
   2026-10-29: on prod it found a ticket's bug may already be gone), so the
   footer never offers to skip it. While Bailey reads, there is no Start plan.
   Queued behind the Team's few readings at a time, Scope now has Bailey read
   it next. Only behind the workspace's spent daily cap may a person plan it
   without Bailey's check, and the footer says who raises the cap: an owner,
   on Settings → Workspace ("Daily Bailey readings", `scopingLimits.ts`), with
   a link for an owner and "ask a workspace owner" for everyone else. */

/** Where a tracker ticket is on its way to Reeve, as the footer tells it. */
export type TrackerSpecWait = "reading" | "queued" | "daily_cap";

/** {@link TrackerSpecWait} from the ticket's `source_ref.trackerSpec`; null once it is past Bailey. */
export function trackerSpecWaitOf(
  sourceRef: Record<string, unknown> | null | undefined,
): TrackerSpecWait | null {
  const spec = sourceRef?.trackerSpec;
  if (!spec || typeof spec !== "object") return null;
  const { status, waitingOn } = spec as Record<string, unknown>;
  if (status === "reading" || status === "proceeding") return "reading";
  if (status !== "queued") return null;
  return waitingOn === "daily_cap" ? "daily_cap" : "queued";
}

/** Bailey is reading it: nothing to press but Keep for a human. */
export function trackerSpecReadingWhy(ticketKey: string | null): string {
  const where = ticketKey ? `on ${ticketKey}` : "on the ticket";
  return `Bailey is checking this ticket against the code and will post the spec ${where}. Reeve plans it next, with nothing for you to accept.`;
}

export const SCOPE_NOW_VERB = "Scope now";

export const TRACKER_SPEC_QUEUED_WHY =
  "Bailey checks this ticket against the code before Reeve plans it, a few of this Team's tickets at a time, and this one is waiting its turn.";

export const TRACKER_SPEC_QUEUED_BOLD =
  "Scope now has Bailey read it next. It still gets Bailey's check.";

/** The one way past Bailey's check, offered only once today's readings are spent. */
export const PLAN_WITHOUT_CHECK_VERB = "Plan without Bailey's check";

export const TRACKER_SPEC_DAILY_CAP_WHY =
  "Bailey checks this ticket against the code before Reeve plans it, but this workspace has used today's readings. Bailey reads it when the daily limit resets at midnight UTC.";

/** Where the limit is raised: the "Daily Bailey readings" field on Settings → Workspace. */
export const DAILY_READINGS_SETTINGS = { href: "/settings", label: "Settings → Workspace" };

/** The way to a higher limit, said to someone who cannot change workspace settings. */
export const TRACKER_SPEC_DAILY_CAP_ASK_OWNER = "Ask a workspace owner to raise the limit.";

/** The bold line over {@link PLAN_WITHOUT_CHECK_VERB}, by the Plan line's rung. */
export function trackerSpecDailyCapBold(planAuthority: JobAuthority | null | undefined): string {
  return `Planning now skips Bailey's reading of the code. ${planSignOffBold(planAuthority)}`;
}

/* ── the two outcomes of a plan gate ──
   The decline on this gate has always run a plan revision server-side and has
   always been labelled "Request changes", which named the request and not the
   consequence. Both outcomes are stated now, and both stay visible: a gate that
   starts execution cannot be undone, so neither half of it is hidden. */

export const PLAN_OUTCOME_REVISE_EFFECT =
  "Sending it back produces the next revision of the plan and re-arms this gate. Nothing runs in the meantime.";

/**
 * The same sentence, naming what it is carrying.
 *
 * The answers are no longer prose pasted into the note box: they travel as
 * their own field on the send-back and reach the next pass as decisions, so
 * the sentence says "carries your N answers" and means it literally. Zero
 * prints the plain one.
 */
export function planReviseEffect(answeredCount: number): string {
  if (answeredCount <= 0) return PLAN_OUTCOME_REVISE_EFFECT;
  const answers = `${answeredCount} answer${answeredCount === 1 ? "" : "s"}`;
  return (
    `Sending it back carries your ${answers} into the next revision of the plan and re-arms ` +
    "this gate. Nothing runs in the meantime."
  );
}

/* ── the deep review ── */

export const REVIEW_HELD_NOTE = "the whole draft waits here · nothing is posted until you say so";

/** The verb on the worker chip above a held draft: "Proctor · drafted this". */
export const REVIEW_DRAFTED_VERB = "drafted this review";

export const REVIEW_TEACH_NOTE = "your edits teach them · what you keep posts as drafted";

export const REVIEW_KEEP = "Keep";
export const REVIEW_EDIT = "Edit";
export const REVIEW_REMOVE = "Remove";

/* The held PR-review gate (YAG-348): confirming posts to GitHub; there is no
   "request changes": the decisions ARE the changes. */
export const REVIEW_CONFIRM_VERB = "Confirm review";
export const REVIEW_CONFIRM_EFFECT =
  "Confirming posts what you kept and edited to the pull request as one review.";
export const REVIEW_CONFIRM_HANDED_OFF = "Review posted to the pull request.";
/** Posted, but the draft stays a draft: its build did not pass everything. */
export const REVIEW_CONFIRM_KEPT_DRAFT =
  "Review posted. The pull request stays a draft because its build did not pass everything; a person takes it out of draft on GitHub.";
/* The confirm route reports the REAL delivery outcome; these render the
   honest non-posted endings. The decisions themselves are always recorded. */
export const REVIEW_CONFIRM_NOT_POSTED_STALE =
  "Decisions recorded. The pull request changed before posting, so nothing was posted.";
export const REVIEW_CONFIRM_NOTHING_KEPT =
  "Decisions recorded. Everything was removed, so there was nothing to post.";
export const REVIEW_CONFIRM_NOT_POSTED_FAILED =
  "Decisions recorded, but posting to GitHub failed. Re-run the review to retry.";

export const REVIEW_DECISION_NOTE: Record<string, string> = {
  keep: "kept as drafted",
  edited: "edited by you · recorded",
  removed: "removed by you · recorded",
};

/**
 * "seven checks ran · two merged · one dropped". Built ONLY from counts the
 * artifact actually recorded; an absent count is left out rather than zeroed,
 * and nothing recorded means no line at all.
 */
export function orchestrationLine(input: {
  checksRan: number | null;
  merged: number | null;
  dropped: number | null;
}): string | null {
  const parts: string[] = [];
  if (input.checksRan != null) {
    parts.push(`${input.checksRan} check${input.checksRan === 1 ? "" : "s"} ran`);
  }
  if (input.merged != null) parts.push(`${input.merged} merged`);
  if (input.dropped != null) parts.push(`${input.dropped} dropped`);
  return parts.length > 0 ? parts.join(" · ") : null;
}

/* ── the journey ── */

export const JOURNEY_LABEL = "The journey";
export const JOURNEY_EMPTY =
  "Nothing was recorded stage by stage for this one. What exists is in the record below.";

/** The marker chip on a journey row. */
export const JOURNEY_WAITED = "waited on an editor";
export const JOURNEY_RAN_ALONE = "ran without waiting";
export const JOURNEY_MERGE_STAYS = "merge decisions stay with people";
export const JOURNEY_YOU = "you";

/** Beside the person's name on a row whose act came through the ticket's chat. */
export const JOURNEY_FROM_CHAT = "from chat";

/** What an act from the ticket's chat reads as on the timeline, by verb. */
export const CHAT_ACT_TITLE: Record<ItemVerb, string> = {
  accept_proposal: "The proposal was accepted",
  decline_proposal: "The proposal was declined",
  approve_plan: "The plan was approved",
  send_back_plan: "The plan was sent back",
  accept_plan_changes: "Plan changes were accepted",
  reject_plan_change: "A plan change was rejected",
  answer: "The ask was answered",
  dismiss_ask: "Closed: the ask was not needed",
  stop_to_planning: "Sent back to planning",
  reoffer_plan: "The plan was offered again",
  continue_anyway: "The build got one more attempt",
  patch_plan: "The plan was patched and the build continued",
  approve_act: "The staged act was approved",
  decline_act: "The staged act was declined",
  start_planning: "Planning was started",
  request_changes: "Changes were requested on the pull request",
  fix_conflicts: "A merge round was started to fix the conflicts",
  run_again: "The line was run again",
  stop_run: "The run was stopped and taken over",
  hand_back: "The ticket was handed back to the Team",
  leave_next_step_note: "A steering note was left for the next step",
  return_to_proposed: "Sent back to Proposed",
  start_over: "The work was discarded to start over",
  close_item: "The ticket was closed",
  split_item: "The ticket was split",
  rerun_review: "Proctor was asked to review again",
  review_again: "A new review was requested",
  edit_fields: "The ticket was edited",
};

const EDITED_FIELD_WORD: Record<string, string> = {
  title: "title",
  description: "description",
  repoId: "repository",
};

/**
 * The timeline line for an act from the ticket's chat, from its record only:
 * the verb, and for an edit the names of the fields it changed and the new
 * priority. Never the person's text. Null for a verb this build does not know.
 */
export function chatActTitle(metadata: Record<string, unknown> | null): string | null {
  const verb = metadata?.verb;
  if (typeof verb !== "string" || !Object.hasOwn(CHAT_ACT_TITLE, verb)) return null;
  if (verb !== "edit_fields") return CHAT_ACT_TITLE[verb as ItemVerb];
  const fields = Array.isArray(metadata?.fields) ? metadata.fields : [];
  const words = fields.flatMap((field) =>
    typeof field === "string" && EDITED_FIELD_WORD[field] ? [EDITED_FIELD_WORD[field]] : [],
  );
  const priority = typeof metadata?.priority === "string" ? metadata.priority : null;
  const parts: string[] = [];
  if (words.length > 0) parts.push(`${listWords(words)} edited`);
  if (priority) parts.push(`priority set to ${priority}`);
  if (parts.length === 0) return CHAT_ACT_TITLE.edit_fields;
  const line = parts.join(", ");
  return line.charAt(0).toUpperCase() + line.slice(1);
}

/* ── the receipt ── */

/** The verbs the "who did what" grid uses, in spine order. */
export const RECEIPT_ROLES = ["proposed", "planned", "built", "reviewed", "settled"] as const;
export type ReceiptRole = (typeof RECEIPT_ROLES)[number];

/* ── plan revisions ─────────────────────────────────────────────────────────
   A plan is rewritten every time a reviewer sends it back, so the reader needs
   to know which one is on screen and whether it is the one their decision acts
   on. The word is "revision" because that is what the plan row calls its
   version, and because "draft" would imply nobody has seen it. */

/**
 * The stored review state, in the receipt's customer vocabulary. The raw
 * enum (`pending_review`, `comments_posted`, ...) is a machine fact — the
 * receipt keeps the fact but speaks it, instead of leaking the internal
 * spelling. One meaning per word, mirroring the derived chip vocabulary.
 * Work-local on purpose: the receipt is this tree's only consumer, and the
 * workers surface must not be a dependency of the work tree for one map.
 */
export const AGENT_REVIEW_STATE_COPY: Record<AgentReviewState, string> = {
  pending_review: "not yet reviewed",
  queued: "queued",
  reviewing: "review in progress",
  comments_posted: "comments posted",
  approved: "approved",
  approval_advised: "approval advised",
  needs_follow_up: "needs follow-up",
};

/* ── the Team page (the Team's own page, not the cross-Team index) ──
   The header is one sentence of Responsibilities and one quiet mono line, and
   everything that explains a fact rides that fact's title attribute rather than
   taking a second line. The needs-you band above the roster is the manager's
   first question answered before anything else on the page. */

/**
 * "Wright used the ticket's budget; waiting on you to decide". The one row sentence
 * for a line's act that spent its per-item cap: the person is not asked to
 * approve the act, they are asked which of three endings this gets.
 */
export function outOfRoundsWaitingClause(agentName: string | null, target: string): string {
  const who = agentName?.trim() || "The line";
  return `${who} used the ticket's budget; waiting on ${target} to decide`;
}

/* ── start a Team on my tickets ──
   The one-click Team: the wizard's Team, working from the tickets of the
   person who pressed it, with Bailey on it and the propose line off. */

/* ── review PRs with Proctor ──
   The other one-click Team: Proctor, and only Proctor, reviewing every pull
   request on the repositories the person picks. No Bailey, no Wright. */

// ── The plan reader (PlanReader) ──
/** The steps' one control: open every titled step, or fold them all back. */
export const PLAN_STEPS_EXPAND_ALL = "Expand all";
export const PLAN_STEPS_COLLAPSE_ALL = "Collapse all";
/** The hover link on a step: a person points at "step 3", so it has an address. */
export const PLAN_STEP_COPY_LINK = (n: number) => `Copy a link to step ${n}`;
export const PLAN_STEP_LINK_COPIED = "Link copied";
export const PLAN_STEP_LINK_FAILED = "Could not copy the link";
/** More files than the step's head has room for. */
export const PLAN_STEP_MORE_FILES = (n: number) => `+${n}`;
/** The files table's columns and the two change kinds. */
export const PLAN_FILES_COL_FILE = "File";
export const PLAN_FILES_COL_CHANGE = "Change";
export const PLAN_FILES_COL_STEPS = "Steps";
export const PLAN_FILE_NEW = "new";
export const PLAN_FILE_EDIT = "edit";
export const PLAN_FILE_TEST = "test";
export const PLAN_FILE_GO_TO_STEP = (n: number) => `Go to step ${n}`;

/* ── Wright waiting on its own pull request ── */

function listWords(words: string[]): string {
  if (words.length <= 1) return words[0] ?? "";
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/* ── the plan's self-review ──
   Before a person sees the plan, the Agent that drafted it checks it in
   rounds against five checks and edits it with typed changes. The case file
   shows every round, closed by default, above the plan. The actor is always
   the planning run's own Agent, passed in by name: these helpers never
   assume who planned. */

/** The most rounds one planning run reviews its plan. The loop's own limit. */
export const SELF_REVIEW_ROUND_LIMIT = 5;

/** The five checks, in the order a round prints them. */
export const SELF_REVIEW_CHECK_LABEL: Record<PlanSelfReviewCheckKey, string> = {
  halfMeasures: "Half-measures",
  complexity: "Extra complexity",
  scope: "Reach beyond scope",
  observability: "Observability",
  failureCases: "Failure cases covered",
  earlierWork: "Earlier work",
};

export const SELF_REVIEW_MAJOR_HEADING = "Major changes";
export const SELF_REVIEW_CHECKS_HEADING = "Five checks";
/** The heading on a round that also held the plan to discarded earlier work. */
export const SELF_REVIEW_CHECKS_HEADING_SIX = "Six checks";
export const SELF_REVIEW_CHANGED_HEADING = "What changed in the plan";
export const SELF_REVIEW_CHECK_HEADING = "Final check";
export const SELF_REVIEW_NO_MAJOR = "none";
export const SELF_REVIEW_CHECK_CLEAR = "no issues";
export const SELF_REVIEW_PLAN_UNCHANGED = "nothing · plan kept as is";
export const SELF_REVIEW_VERDICT_CHIP = {
  approved: "approved",
  changes_requested: "changes requested",
} as const;
export const SELF_REVIEW_FOOT =
  "Round 1 checks the first draft. Each later round checks the previous revision.";
export const SELF_REVIEW_FAILED_HEAD = "Did not finish";
export const SELF_REVIEW_FAILED_NEXT =
  "Nothing retries this round on its own. Sending the plan back starts a new draft with fresh rounds.";

/** Who the sentences name when the roster cannot name the planning run's Agent. */
const SELF_REVIEW_NAMELESS = "The Agent";

/** The row's label: "Reeve's self-review". */
export function selfReviewLabel(name: string | null): string {
  return name ? `${name}'s self-review` : "Self-review";
}

function roundsWord(n: number): string {
  return `${n} round${n === 1 ? "" : "s"}`;
}

const TIMES = ["", "once", "twice", "three times", "four times", "five times"];
function timesWord(n: number): string {
  return TIMES[n] ?? `${n} times`;
}

/** What a set of rounds adds up to, counted off the rounds themselves. */
export interface SelfReviewFacts {
  outcome: PlanSelfReviewOutcome;
  /** Rounds on the record. */
  rounds: number;
  /** The round running now, while the planning run is live. */
  runningRound: number | null;
  /** Major changes made across every round. */
  majorChanges: number;
  /** Rounds whose final check asked for changes. */
  checkAsks: number;
  /** The round that did not finish, when one did not. */
  failedRound: number | null;
  /** The last round made a major change. */
  lastMadeMajor: boolean;
  /** The final check's verdict on the last round, when the check ran there. */
  lastCheck: "approved" | "changes_requested" | null;
  /** The most recent check's verdict, on whichever round it ran. */
  latestCheck?: "approved" | "changes_requested" | null;
}

/** The plan a failed review left in place. */
function keptPlan(failedRound: number): string {
  return failedRound > 1 ? `round ${failedRound - 1}'s revision` : "the first draft";
}

/**
 * The closed row's one line: "3 rounds · settled on round 3 · 3 major changes
 * made". Every number is counted from the rounds on the record.
 */
export function selfReviewSummary(f: SelfReviewFacts): string {
  switch (f.outcome) {
    case "running":
      return f.runningRound
        ? `round ${f.runningRound} of up to ${SELF_REVIEW_ROUND_LIMIT} · running`
        : `${roundsWord(f.rounds)} · running`;
    case "converged": {
      if (f.checkAsks > 0) {
        const more = f.checkAsks === 1 ? "one more" : `${f.checkAsks} more`;
        return `${roundsWord(f.rounds)} · the check asked for ${more} · settled on round ${f.rounds}`;
      }
      if (f.rounds === 1 && f.majorChanges === 0) return "1 round · no major changes";
      const made =
        f.majorChanges > 0
          ? ` · ${f.majorChanges} major change${f.majorChanges === 1 ? "" : "s"} made`
          : "";
      return `${roundsWord(f.rounds)} · settled on round ${f.rounds}${made}`;
    }
    case "cap_reached": {
      const why = f.lastMadeMajor
        ? " · last round still made a major change"
        : f.lastCheck === "changes_requested"
          ? " · the check still asked for changes"
          : "";
      return `${roundsWord(f.rounds)} · stopped at the limit${why}`;
    }
    case "unchanged":
      return `${roundsWord(f.rounds)} · the plan stopped changing${
        f.latestCheck === "changes_requested" ? " · the check still asked for changes" : ""
      }`;
    case "time_budget":
      return `${roundsWord(f.rounds)} · ran out of time`;
    case "error":
      return f.failedRound
        ? `${roundsWord(f.rounds)} · round ${f.failedRound} did not finish · plan is ${keptPlan(f.failedRound)}`
        : `${roundsWord(f.rounds)} · did not finish`;
    case "stopped":
      return `${roundsWord(f.rounds)} · stopped`;
    case "none":
      return "";
  }
}

/** The first line inside the open row: what the rounds were, in one or two sentences. */
export function selfReviewIntro(f: SelfReviewFacts, name: string | null): string {
  const who = name ?? SELF_REVIEW_NAMELESS;
  switch (f.outcome) {
    case "running":
    case "none":
      return (
        `${who} checks its first draft before you see it. The plan lands here when a round ` +
        `finds nothing major and the check approves, or after ${SELF_REVIEW_ROUND_LIMIT} rounds.`
      );
    case "converged":
      if (f.checkAsks > 0) {
        return (
          `${who} checked its first draft ${timesWord(f.rounds)} before you saw it. A round with no ` +
          `major change runs the final check, and when the check asks for changes, the next round ` +
          `makes them. The check approved round ${f.rounds}.`
        );
      }
      if (f.rounds === 1 && f.majorChanges === 0) {
        return `${who} checked its first draft once and found nothing major to change.`;
      }
      return (
        `${who} checked its first draft ${timesWord(f.rounds)} before you saw it. Each round looked ` +
        `for five kinds of problems and revised the plan when it found one. It stopped on the first ` +
        `round with no major changes.`
      );
    case "cap_reached":
      return `${who} checked its draft ${timesWord(f.rounds)}, the most it will.`;
    case "unchanged":
      return `${who} checked its draft ${timesWord(f.rounds)} and stopped when two rounds in a row found nothing to change.`;
    case "time_budget":
      return `${who} checked its draft ${timesWord(f.rounds)} before its time ran out.`;
    case "error":
      if (f.failedRound === 1)
        return `${who} started checking its first draft, and round 1 did not finish.`;
      if (f.failedRound) {
        return `${who} checked its first draft ${timesWord(f.failedRound - 1)}. Round ${f.failedRound} did not finish.`;
      }
      return `${who} checked its first draft ${timesWord(f.rounds)}, then the review stopped without finishing.`;
    case "stopped":
      return `${who} checked its draft ${timesWord(f.rounds)}, then the run was stopped.`;
  }
}

/**
 * The amber note: the review did not end the way it is meant to (the limit,
 * the clock, or a round that did not finish), so the plan below carries
 * something nobody re-checked. Null when it ended cleanly.
 */
export function selfReviewAmberNote(
  f: SelfReviewFacts,
  name: string | null,
  failedMessage: string | null,
): string | null {
  const who = name ?? SELF_REVIEW_NAMELESS;
  if (f.outcome === "error" || f.failedRound) {
    if (!f.failedRound) {
      return `The review stopped after round ${f.rounds} without finishing. The plan below is round ${f.rounds}'s revision.`;
    }
    const why = failedMessage ? ` ${failedMessage}` : "";
    return `Round ${f.failedRound} did not finish.${why} The plan below is ${keptPlan(f.failedRound)}.`;
  }
  if (f.outcome === "cap_reached") {
    const head = `${who} stops after ${SELF_REVIEW_ROUND_LIMIT} rounds.`;
    if (f.lastMadeMajor) {
      if (f.lastCheck === "approved") {
        return `${head} Round ${f.rounds} still made a major change, and the final check approved that revision.`;
      }
      if (f.lastCheck === "changes_requested") {
        return `${head} Round ${f.rounds} still made a major change, and the final check asked for changes no round was left to make. Read the plan closely before you approve.`;
      }
      return `${head} Round ${f.rounds} still made a major change, so nothing checked that last revision. Read the plan closely before you approve.`;
    }
    return `${head} The check on round ${f.rounds} still asked for changes, and no round was left to make them. Read its notes in round ${f.rounds} before you approve.`;
  }
  if (f.outcome === "unchanged" && f.latestCheck === "changes_requested") {
    return "The check still asked for changes, and two rounds in a row found nothing to change. Read its notes before you approve.";
  }
  if (f.outcome === "time_budget") {
    const check =
      f.lastCheck === "approved"
        ? "The final check approved the last revision."
        : f.lastCheck === "changes_requested"
          ? "The final check asked for changes no round was left to make."
          : "Nothing checked the last revision.";
    return `${who} ran out of time after round ${f.rounds}. ${check}`;
  }
  return null;
}

/** A running round's line, before it has a headline of its own. */
export function selfReviewRunningHead(round: number, status: "reviewing" | "checking"): string {
  if (status === "checking") return "Running the final check…";
  return round === 1 ? "Checking the first draft…" : "Checking the revised plan…";
}

/** A done round with no headline: what it found, counted. */
export function selfReviewDoneHead(majorChanges: number): string {
  return majorChanges > 0
    ? `${majorChanges} major change${majorChanges === 1 ? "" : "s"}`
    : "No major changes";
}

/** The next round, while the current one runs. */
export function selfReviewPendingHead(previousRound: number): string {
  return `Runs only if round ${previousRound} makes a major change · up to round ${SELF_REVIEW_ROUND_LIMIT}`;
}

/** The round row's right edge: "2 major · plan revised · check approved". */
export function selfReviewRoundNote(round: {
  state: "running" | "failed" | "done";
  majorChanges: number;
  revised: boolean;
  check: "approved" | "changes_requested" | null;
  duration: string | null;
}): string {
  const parts: string[] = [];
  if (round.state === "running") parts.push("running");
  else if (round.state === "failed") parts.push("did not finish");
  else {
    parts.push(round.majorChanges > 0 ? `${round.majorChanges} major` : "clear");
    if (round.revised) parts.push("plan revised");
    if (round.check === "approved") parts.push("check approved");
    if (round.check === "changes_requested") parts.push("check: changes requested");
  }
  if (round.duration) parts.push(round.duration);
  return parts.join(" · ");
}

/** The open row's clock while the review still runs: "4m 22s so far". */
export function selfReviewSoFar(duration: string): string {
  return `${duration} so far`;
}

/** Why a round reviewed the plan without reading the code, by the code the loop stored. */
const SELF_REVIEW_FALLBACK_REASON: Record<string, string> = {
  review_daily_cap_reached: "the workspace reached its daily review limit",
  review_budget_spent: "this plan's review budget was spent",
  session_failed: "the code reading session failed",
  session_timed_out: "the code reading session ran out of time",
  invalid_output: "the code reading session's answer could not be used",
  no_repository_reading: "the plan was written without reading the repository",
  repo_not_eligible: "the repository cannot be read from here",
  budget_invalid: "the Team's planning budget is too small for a code reading session",
};

/** A round that fell back to the notes: "Checked without reading the code, because …". */
export function selfReviewFallbackNote(reason: string | null): string {
  const why = reason ? SELF_REVIEW_FALLBACK_REASON[reason] : undefined;
  return why
    ? `Checked without reading the code, because ${why}.`
    : "Checked without reading the code.";
}

/** The plan lists a round can change, in the chip's words. */
const SELF_REVIEW_LIST_WORD: Record<keyof PlanSelfReviewDelta["lists"], string> = {
  acceptanceCriteria: "criteria",
  filesToChange: "files",
  commandsToRun: "commands",
  questions: "questions",
  steps: "steps",
};

/** "files −1 +1": what a round took out of and put into one plan list. Null when it did neither. */
export function selfReviewListChip(
  list: keyof PlanSelfReviewDelta["lists"],
  added: number,
  removed: number,
): string | null {
  if (added <= 0 && removed <= 0) return null;
  const parts = [removed > 0 ? `−${removed}` : "", added > 0 ? `+${added}` : ""].filter(Boolean);
  return `${SELF_REVIEW_LIST_WORD[list]} ${parts.join(" ")}`;
}

/* ── Proctor and Wright on the pull request (YAG-873) ──
   What the ticket sheet says while the pull request is reviewed and fixed.
   Only what the record shows: a review that has not posted is said to be
   running, never described as a review. */

/** The label over the build run while Proctor's run is the one the sheet follows. */
export const EARLIER_RUN_LABEL = "Earlier run";

/** "PR #3817", or "the pull request" when the number is not known. */
function prWords(prNumber: number | null | undefined): string {
  return prNumber != null ? `PR #${prNumber}` : "the pull request";
}

/** "Proctor is lined up to review PR #3817". */
export function proctorQueuedLine(reviewer: string, prNumber: number | null | undefined): string {
  return `${reviewer} is lined up to review ${prWords(prNumber)}`;
}

/**
 * "Proctor is reviewing PR #3817 at cda6309 · started 8:28 PM · attempt 4".
 * Each fact rides only when the record has it; the attempt only past the first.
 */
export function proctorReviewingLine(facts: {
  reviewer: string;
  prNumber: number | null | undefined;
  headSha: string | null | undefined;
  startedClock: string | null;
  attempt: number | null | undefined;
}): string {
  const head = `${facts.reviewer} is reviewing ${prWords(facts.prNumber)}${
    facts.headSha ? ` at ${facts.headSha.slice(0, 7)}` : ""
  }`;
  return [
    head,
    facts.startedClock ? `started ${facts.startedClock}` : null,
    typeof facts.attempt === "number" && facts.attempt > 1 ? `attempt ${facts.attempt}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * "Wright is fixing 3 findings · round 2"; the count is left out when the round
 * did not say. No "of N": rounds draw from the ticket's budget, not a cap.
 */
export function wrightFixingLine(builder: string, findings: number | null, round: number): string {
  const what =
    findings == null ? "the findings" : `${findings} finding${findings === 1 ? "" : "s"}`;
  return `${builder} is fixing ${what} · round ${round}`;
}

/**
 * "Wright is fixing 2 asks · round 3": a one-attempt lane fix round, which
 * takes what a review, a person or red CI asked; "the asks" when the round's
 * record did not count them.
 */
export function wrightFixingAsksLine(builder: string, asks: number | null, round: number): string {
  const what = asks == null ? "the asks" : `${asks} ask${asks === 1 ? "" : "s"}`;
  return `${builder} is fixing ${what} · round ${round}`;
}

/** "Needs your decision: 2 findings". */
export function decisionFindingsLine(count: number): string {
  return `Needs your decision: ${count} finding${count === 1 ? "" : "s"}`;
}

export const REVIEW_NEXT_RELEASED = "A person took this over";
export const REVIEW_NEXT_WAITING = "Reviewed, waiting on the next push";

/**
 * What a ticket in Review waits on when nothing is running, one sentence per
 * answer of `workStage.reviewWaitOf`. The row prints it lowercased after the
 * stage word ("Review · approved, ready to merge"); the case file's pill and
 * the sheet's Proctor line print it as is. Only `push` promises a push: it is
 * said only while findings a fix round will address are open.
 */
export const REVIEW_WAIT_WORDS = {
  person: "Reviewed, waiting on a person",
  push: REVIEW_NEXT_WAITING,
  draft: "The pull request is still a draft",
  proctor: "Waiting on the review",
  ready: "Approved, ready to merge",
  merge: "Reviewed, waiting on a person to approve or merge",
} as const;

/** A finished review whose pull request the read could not place: the one fact known. */
export const REVIEW_WAIT_UNKNOWN = "Reviewed";
export const REVIEW_HELD_FOR_PERSON = "Waiting for a person to publish Proctor's review";
export const REVIEW_HELD_OPEN = "Open the review";

/* ── Start over: discard a ticket's work and plan it again ── */

export const START_OVER_MENU = "Start over";
export const startOverTitle = (key: string) => `Start over on ${key}?`;
/** What Start over does, by the stage the work has reached. */
export function startOverEffect(stage: "plan" | "build" | "review", hasTracker: boolean): string {
  const tracker = hasTracker ? " The tracker ticket stays open and gets a comment." : "";
  if (stage === "build") {
    return `Wright's build stops now and is thrown away. Reeve plans the ticket again from the start, with your reason.${tracker}`;
  }
  if (stage === "plan") {
    return `Reeve's plan is thrown away, and Reeve plans again from the start, with your reason.${tracker}`;
  }
  return `Wright's work is thrown away, and Reeve plans the ticket again from the start. The next plan and build read your reason.${tracker}`;
}
export const START_OVER_REASON_LABEL = "What was wrong with this work?";
export const START_OVER_REASON_NEEDED =
  "Reeve's next plan starts from your reason, so say what was wrong.";
export const START_OVER_REASON_PLACEHOLDER = "Say what the next plan should do differently";
export const startOverClosePr = (n: number) => `Close pull request #${n} on GitHub`;
export const START_OVER_CLOSE_PR_ON = "A comment on it gives your reason.";
export const START_OVER_CLOSE_PR_OFF = "It stays open, as it is now.";
export const START_OVER_DELETE_BRANCH = "Delete its branch";
export const START_OVER_DELETE_BRANCH_ON = "GitHub keeps the commits on the closed pull request.";
export const START_OVER_DELETE_BRANCH_LOCKED =
  "Deleting the branch would close the pull request too, so it stays while the pull request stays open.";
export const START_OVER_CONFIRM = "Discard and start over";
export const START_OVER_DONE = "Discarded. Reeve is planning it again.";
export const START_OVER_HELD =
  "Discarded. The new plan waits for a free slot and starts on its own.";
export const START_OVER_NOT_STARTED =
  "Discarded. Planning did not start. Press Start plan on the new attempt.";
/** The toast after Start over, by what planning did. */
export function startOverToast(planning: "started" | "held" | "not_started"): string {
  if (planning === "held") return START_OVER_HELD;
  if (planning === "not_started") return START_OVER_NOT_STARTED;
  return START_OVER_DONE;
}
export const START_OVER_FAILED = "The ticket could not be started over. Try again.";

/* ── Steer: a person's note for the running work's next step (YAG-1116) ── */

export const ADD_NOTE_LABEL = "Steer";
export const addNoteTitle = (key: string) => `Steer ${key}`;
export const ADD_NOTE_EFFECT =
  "Wright reads it at the next build step or fix round, and the timeline says when it did. The step running now does not stop.";
export const ADD_NOTE_FIELD = "Your note";
export const ADD_NOTE_PLACEHOLDER = "What should the next step take into account?";
export const ADD_NOTE_REPLACES = "A steering note that is still waiting is replaced by this one.";
export const ADD_NOTE_CONFIRM = "Send the note";
export const ADD_NOTE_FAILED = "The steering note was not left. Try again.";
/** The timeline's second line under the stage running now, once its session read a note. */
export const TIMELINE_NOTE_READ = "Read the steering note a person added while it worked";
/**
 * The toast after a steering note lands, by whether a step is coming to read
 * it (the route's `stepComing`), in the chat's words (`tools/native/itemRail.ts`).
 */
export function addNoteToast(key: string, stepComing: boolean | undefined): string {
  if (stepComing === false) {
    return `Steering note left for ${key}, but nothing is running on it, so nothing will read it yet. It reaches the next build step or fix round only when one starts.`;
  }
  return `Steering note left for ${key}; it reaches the next build step or fix round.`;
}
/**
 * The live pane's line under its sentence while a steering note waits for
 * the next step (YAG-1127), by the note's first line.
 */
export function steeringNoteWaitingLine(firstLine: string): string {
  return `Steering note waiting: '${firstLine}'. Reaches the next step.`;
}
/** Who left the waiting note and when, beside the line. */
export function steeringNoteLeftBy(authorName: string | null, clock: string | null): string {
  return ["Left", authorName ? `by ${authorName}` : null, clock ? `at ${clock}` : null]
    .filter(Boolean)
    .join(" ");
}
/**
 * The Live feed's row the moment a steering note reached the session, by the
 * shared words the backend writes on the beat (`STEERING_NOTE_HANDED_LINE`).
 */
export function steeringNoteHandedRow(clock: string): string {
  return clock ? `${STEERING_NOTE_HANDED_LINE} · ${clock}` : STEERING_NOTE_HANDED_LINE;
}

export const DISCARDED_BEFORE_TITLE = "Discarded before";
export const DISCARDED_BEFORE_READ_BY =
  "Reeve's plan and Wright's build read these before they start.";
export const DISCARDED_NO_REASON = "No reason given.";
export const DISCARDED_PILL = "Discarded";
export const DISCARDED_FOOTER = "Discarded and started over. The new plan carries on in its place.";
export const DISCARDED_WHY_HEADING = "Why it was discarded";
export const OPEN_NEW_PLAN = "Open the new plan";
/** "was in Review · PR #2378 closed, branch deleted" */
export function discardedFacts(discard: {
  phase: string;
  prNumber: number | null;
  pullRequest: { closeRequested: boolean; closed: boolean; branchDeleted: boolean };
}): string {
  const stage: Record<string, string> = {
    accepted: "Plan",
    planning: "Plan",
    awaiting_execution_approval: "Plan",
    executing: "Build",
    needs_input: "Build",
    final_review: "Review",
  };
  const parts = [`was in ${stage[discard.phase] ?? "progress"}`];
  if (discard.prNumber != null) {
    const pr = discard.pullRequest.closed
      ? discard.pullRequest.branchDeleted
        ? "closed, branch deleted"
        : "closed"
      : discard.pullRequest.closeRequested
        ? "not closed yet"
        : "left open";
    parts.push(`PR #${discard.prNumber} ${pr}`);
  }
  return parts.join(" · ");
}
